import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { SnackBarService } from 'src/app/framework/service/snack-bar.service';
import { AnalysisRequestReviewComponent } from '../analysis-request-review/analysis-request-review.component';
import { PatientService } from '../patient/patient.service';

@Component({
  selector: 'app-analysis-request',
  templateUrl: './analysis-request.component.html',
  styleUrls: ['./analysis-request.component.scss']
})
export class AnalysisRequestComponent implements OnInit {
  analysisForm: FormGroup;
  packageList: any[] = [];

  constructor(
    private readonly pservice: PatientService,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly snackBar: SnackBarService,
    private readonly dialog: MatDialog,
    public dialogRef: MatDialogRef<AnalysisRequestComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    this.analysisForm = this.fb.group({});

    // If we have pre-selected data (coming back from review), restore it
    if (this.data.selectedPackages && this.data.selectedPackages.length > 0) {
      this.loadPackagesWithPreselection();
    } else {
      this.loadAllPackages();
    }
  }

  /** Load all packages without any preselection */
  private loadAllPackages(): void {
    this.pservice.getAllPackageTemplates().subscribe({
      next: (res: any[]) => {
        this.packageList = res.map(pkg => ({
          ...pkg,
          selected: false,
          templates: pkg.templates.map(t => ({ ...t, selected: false }))
        }));
      },
      error: err => this.snackBar.error('Failed to load packages: ' + err.message)
    });
  }

  /** Load packages and restore selections from review */
  private loadPackagesWithPreselection(): void {
    this.pservice.getAllPackageTemplates().subscribe({
      next: (res: any[]) => {
        this.packageList = res.map(pkg => ({
          ...pkg,
          selected: false,
          indeterminate: false,
          templates: pkg.templates.map(t => ({ ...t, selected: false }))
        }));

        // Restore selections from data.selectedPackages
        this.restoreSelections();
      },
      error: err => this.snackBar.error('Failed to load packages: ' + err.message)
    });
  }

  /** Restore selections based on data.selectedPackages */
  private restoreSelections(): void {
    this.data.selectedPackages.forEach(selectedPkg => {
      const pkg = this.packageList.find(p => p.packageId === selectedPkg.packageId);
      if (pkg) {
        selectedPkg.templateIds.forEach(templateId => {
          const template = pkg.templates.find(t => t.templateId === templateId);
          if (template) {
            template.selected = true;
          }
        });
        
        // Update package selection state
        this.toggleTemplate(pkg);
      }
    });
  }

  /** When package checkbox toggled */
  togglePackage(pkg: any): void {
    pkg.templates.forEach(t => t.selected = pkg.selected);
    pkg.indeterminate = false; // reset indeterminate when full toggle
  }

  toggleTemplate(pkg: any): void {
    const selectedCount = pkg.templates.filter(t => t.selected).length;

    if (selectedCount === pkg.templates.length) {
      // all selected
      pkg.selected = true;
      pkg.indeterminate = false;
    } else if (selectedCount === 0) {
      // none selected
      pkg.selected = false;
      pkg.indeterminate = false;
    } else {
      // partially selected
      pkg.selected = false;
      pkg.indeterminate = true;
    }
  }

  get selectedCount(): number {
    return this.packageList.reduce((count, pkg) => {
      return count + pkg.templates.filter(t => t.selected).length;
    }, 0);
  }

  onSubmit(): void {
    const selectedData = this.packageList
      .filter(pkg =>
        pkg.selected || pkg.templates.some(t => t.selected)
      )
      .map(pkg => ({
        packageId: pkg.packageId,
        packageName: pkg.packageName,
        templateIds: pkg.templates
          .filter(t => t.selected)
          .map(t => t.templateId),
        templateNames: pkg.templates
          .filter(t => t.selected)
          .map(t => t.templateName)
      }));

    if (selectedData.length === 0) {
      this.snackBar.error('Please select at least one template');
      return;
    }

    this.dialogRef.close();

    // Open review dialog and pass the callback through
    const reviewDialogRef = this.dialog.open(AnalysisRequestReviewComponent, {
      width: '630px',
      disableClose: true,
      autoFocus: false,
      data: {
        patientId: this.data.patientId,
        selectedPackages: selectedData,
      }
    });

    reviewDialogRef.afterClosed().subscribe((result) => {

      if (result && result.action === 'back') {
        // Reopen the selection dialog with the selections returned from review
        this.dialog.open(AnalysisRequestComponent, {
          width: '630px',
          disableClose: true,
          autoFocus: false,
          data: {
            patientId: this.data.patientId,
            selectedPackages: result.selectedPackages,
          }
        });
      } else if (result && result !== 'back') {
        // User confirmed - submit the final request
        this.submitFinalRequest(result);
      }
    });
  }

  private submitFinalRequest(payload: any): void {
    
  }

  close(): void {
    this.dialogRef.close(false);
  }
}